/** Send ESC-POS bytes to a previously paired USB receipt printer (WebUSB). */

function parseUsbDeviceInfo(
	deviceInfo: unknown,
): { vendorId: number; productId: number } | null {
	if (!deviceInfo) return null;
	let info: any = deviceInfo;
	if (typeof info === 'string') {
		try {
			info = JSON.parse(info);
		} catch {
			return null;
		}
	}
	const vendorId = Number(info?.vendorId);
	const productId = Number(info?.productId);
	if (!Number.isFinite(vendorId) || !Number.isFinite(productId)) return null;
	return { vendorId, productId };
}

function base64ToBytes(base64: string): Uint8Array {
	const binary = atob(base64);
	const bytes = new Uint8Array(binary.length);
	for (let i = 0; i < binary.length; i++) {
		bytes[i] = binary.charCodeAt(i);
	}
	return bytes;
}

/**
 * Open the Zadig printer the same way print bill does: paired device, configuration 1, interface 0.
 */
export async function openPairedUsbPrinter(deviceInfo: unknown): Promise<any> {
	const info = parseUsbDeviceInfo(deviceInfo);
	if (!info) {
		throw new Error('USB printer is not paired. Connect it in Receipt Printer settings.');
	}
	const usb = (navigator as any)?.usb;
	if (!usb?.getDevices) {
		throw new Error('WebUSB is not available in this browser.');
	}
	const devices = await usb.getDevices();
	const device = devices.find(
		(d: any) => d.vendorId === info.vendorId && d.productId === info.productId,
	);
	if (!device) {
		throw new Error(
			'USB printer not found. Connect it in Receipt Printer settings, then try again.',
		);
	}
	if (!device.opened) await device.open();
	await device.selectConfiguration(1);
	if (!device.configuration?.interfaces?.[0]?.claimed) {
		await device.claimInterface(0);
	}
	return device;
}

/** Release the Zadig printer the same way print bill disconnects: interface 0, then close. */
export async function closeUsbPrinter(device: any): Promise<void> {
	if (!device) return;
	try {
		if (device.opened && device.configuration?.interfaces?.[0]?.claimed) {
			await device.releaseInterface(0);
		}
	} catch {
		/* interface may already be released */
	}
	try {
		if (device.opened) await device.close();
	} catch {
		/* device may already be closed */
	}
}

/**
 * Open the saved USB printer and send a complete ESC-POS job (including cut).
 * The printer must already have been paired from Receipt Printer settings.
 */
export async function sendEscPosBase64ToWebUsb(
	deviceInfo: unknown,
	base64: string,
): Promise<void> {
	const info = parseUsbDeviceInfo(deviceInfo);
	if (!info) {
		throw new Error('USB printer is not paired. Connect it in Receipt Printer settings.');
	}
	const usb = (navigator as any)?.usb;
	if (!usb?.getDevices) {
		throw new Error('WebUSB is not available in this browser.');
	}

	const devices = await usb.getDevices();
	const device = devices.find(
		(d: any) => d.vendorId === info.vendorId && d.productId === info.productId,
	);
	if (!device) {
		throw new Error(
			'USB printer not found. Connect it in Receipt Printer settings, then try again.',
		);
	}

	const openedHere = !device.opened;
	let claimedHere = false;
	let interfaceNumber = 0;

	try {
		if (openedHere) {
			await device.open();
		}
		if (!device.configuration) {
			await device.selectConfiguration(1);
		}

		const iface = device.configuration?.interfaces?.[0];
		if (!iface) {
			throw new Error('No USB printer interface found.');
		}
		interfaceNumber = iface.interfaceNumber ?? 0;

		if (!iface.claimed) {
			await device.claimInterface(interfaceNumber);
			claimedHere = true;
		}

		const outEndpoint = iface.alternates?.[0]?.endpoints?.find(
			(ep: any) => ep.direction === 'out',
		);
		if (!outEndpoint) {
			throw new Error('USB printer OUT endpoint not found.');
		}

		const bytes = base64ToBytes(base64);
		const chunkSize = 4096;
		for (let offset = 0; offset < bytes.length; offset += chunkSize) {
			await device.transferOut(
				outEndpoint.endpointNumber,
				bytes.subarray(offset, offset + chunkSize),
			);
		}
		await new Promise(resolve => setTimeout(resolve, 300));
	} finally {
		if (claimedHere) {
			try {
				await device.releaseInterface(interfaceNumber);
			} catch {
				/* interface may already be released */
			}
		}
		if (openedHere && device.opened) {
			try {
				await device.close();
			} catch {
				/* device may already be closed */
			}
		}
	}
}
